import { Logger } from '../../../shared/utils/logger'
import { PRODUCT_CATEGORY } from '../../product/constants/productCategories.js'

import { CATALOG_DISPLAY_REGISTRY } from '../registries/catalogDisplayRegistry.js'
import { FILTER_REGISTRY } from '../registries/filterRegistry.js'
import { SORT_REGISTRY }    from '../registries/sortRegistry.js'
import { BASE_MAIN_FILTER_IDS } from '../constants/filterConstants.js'
import { VIEW_MODE } from './viewModes.js'


// ── Null object — returned before config is available ─────────────────────────

const NULL_CAPABILITIES = {
  additionalFilters:            [],
  areSoldOutBuildingsActive:    false,
  areSoldOutTiersActive:        false,
  cardDataItems:                [], 
  catalogFilters:               [],
  catalogSortingParameters:     [],
  defaultProductCategory:       PRODUCT_CATEGORY.APARTMENT_STANDARD,
  hasActiveCatalogActions:      false,
  hasArrangeCommissioned:       false,
  hasAxonometricCommissioned:   false,
  hasHotspotCommissioned:       false,
  hasInsolationCommissioned:    false,
  hasMultipleAreas:             false,
  hasMultipleBuildings:         false,
  hasMultipleGates:             false,
  hasMultipleTierSets:          false,
  listColumns:                  [],
  listViewStickyColumnsCount:   0,
  loadWithExpandedFilters:      false,
  loadViewModeDesktop:          VIEW_MODE.LIST,
  loadViewModeMobile:           VIEW_MODE.CARDS,
  mainFilters:                  [], 
  pricesReservedVisible:        false,
  pricesSoldVisible:            false,
  pricesVisible:                false,
  promotedFilters:              [], 
  tileDataItems:                [], 
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function getProductCompartments(product) {
  return asArray(product?.compartments)
}

function hasSunRanges(value) {
  return asArray(value).some((entry) => Array.isArray(entry) && entry.length === 2)
}

function hasInsolationData(product) {
  if (hasSunRanges(product?.sun_light_ranges)) return true

  const tiersHasSun = asArray(product?.tiers).some((tier) => {
    const windows = asArray(tier?.windows)
    return windows.some((windowItem) => hasSunRanges(windowItem?.sun_light_ranges))
  })
  if (tiersHasSun) return true

  return getProductCompartments(product).some((compartment) => hasSunRanges(compartment?.sun_light_ranges))
}


// ── Derivation helpers ────────────────────────────────────

/**
 * Context passed to CATALOG_DISPLAY_REGISTRY `isSupported` methods during derivation.
 *
 * @typedef {Object} DisplaySupportContext
 * @property {boolean} [inquiryEnabled]          - Whether inquiry feature is active in CMS.
 * @property {boolean} [hasActiveCatalogActions] - Whether at least one row action is active.
 * @property {boolean} [hasMultipleBuildings]   - Whether dataset has products in multiple buildings.
 * @property {boolean} [hasMultipleGates]       - Whether dataset has products in multiple gates.
 * @property {boolean} [pricesVisible]           - Whether prices display is globally enabled.
 */

/**
 * Derived display items capability output per view mode.
 *
 * @typedef {Object} CatalogDisplayCapabilities
 * @property {Array<{ id: string }>} activeCatalogActions - Active row action definitions sorted by CMS order.
 * @property {boolean} hasActiveCatalogActions            - True if activeCatalogActions is non-empty.
 * @property {Array<Object>} listColumns                  - Display registry entries for List / Table view.
 * @property {Array<Object>} cardDataItems                 - Display registry entries for Cards view.
 * @property {Array<Object>} tileDataItems                 - Display registry entries for Tiles view.
 */

/**
 * Transforms raw panelSettings catalog_list_columns, catalog_card_data, and catalog_tile_data
 * into clean, normalized display item capabilities for each view mode.
 *
 * Filters items against `isSupported` guards and merges them with active CMS order/visibility.
 *
 * @param {Object}                params
 * @param {DisplaySupportContext} [params.capabilities]  - Context flags derived from dataset & config
 * @param {Object|null}           [params.panelSettings] - Configuration object from panel-settings.json
 * @param {Object|null}           [params.projectConfig] - Configuration object from panel-config.json
 * @returns {CatalogDisplayCapabilities}
 */
function deriveCatalogDisplay({ 
  capabilities = {}, 
  panelSettings, 
  projectConfig, 
}) {
  // 1. Raw CMS / default configurations
  const rawListColumns =
    panelSettings?.catalog_list_columns?.columns ||
    projectConfig?.settings_defaults?.catalog_list_columns?.columns

  const rawActions =
    panelSettings?.catalog_actions?.actions ||
    projectConfig?.settings_defaults?.catalog_actions?.actions

  const rawCardItems =
    panelSettings?.catalog_card_data?.items ||
    projectConfig?.settings_defaults?.catalog_card_data?.items

  const rawTileItems =
    panelSettings?.catalog_tile_data?.items ||
    projectConfig?.settings_defaults?.catalog_tile_data?.items

  // 2. Active actions list & flag
  const activeCatalogActions = (Array.isArray(rawActions) ? rawActions : [])
    .filter((action) => Boolean(action?.is_active) && Boolean(action?.id))
    .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
    .map((action) => ({ id: action.id }))

  const hasActiveCatalogActions = activeCatalogActions.length > 0

  // 3. Filter registry by technical / dataset support
  const supportedContext = { ...capabilities, hasActiveCatalogActions }

  const supportedEntries = CATALOG_DISPLAY_REGISTRY.filter((entry) => {
    if (typeof entry.isSupported !== 'function') return true
    return entry.isSupported(supportedContext)
  })

  // 4. Helper to map supported entries against CMS config overlay per view mode
  function deriveDisplayItemsForView(viewMode, cmsConfig, entries) {
    const viewRegistryMap = new Map(
      entries
        .filter((entry) => Array.isArray(entry.supportedViews) && entry.supportedViews.includes(viewMode))
        .map((entry) => [entry.id, entry])
    )

    if (Array.isArray(cmsConfig) && cmsConfig.length > 0) {
      return cmsConfig
        .filter((item) => item?.is_active !== false && viewRegistryMap.has(item?.id))
        .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
        .map((item) => viewRegistryMap.get(item.id))
        .filter(Boolean)
    }

    // Fallback: return all supported entries for this view mode
    return Array.from(viewRegistryMap.values())
  }

  return {
    activeCatalogActions,
    hasActiveCatalogActions,
    listColumns: deriveDisplayItemsForView(VIEW_MODE.LIST, rawListColumns, supportedEntries),
    cardDataItems: deriveDisplayItemsForView(VIEW_MODE.CARDS, rawCardItems, supportedEntries),
    tileDataItems: deriveDisplayItemsForView(VIEW_MODE.TILES, rawTileItems, supportedEntries),
  }
}


/**
 * Context passed to FILTER_REGISTRY `isSupported` methods during derivation.
 *
 * @typedef {Object} FilterSupportContext
 * @property {boolean} [hasMultipleAreas]       - Whether dataset has products with different floor areas.
 * @property {boolean} [hasMultipleBuildings]   - Whether dataset has products in multiple buildings.
 * @property {boolean} [hasMultipleGates]       - Whether dataset has products in multiple gates.
 * @property {boolean} [hasMultipleTiers]       - Whether dataset has products on different floor/floor sets (multistory).
 * @property {boolean} [isInsolationSupported]  - Whether insolation presentation was commissioned and data is present.
 * @property {boolean} [pricesVisible]           - Whether prices display is globally enabled.
 */

/**
 * Transforms raw panelSettings catalog_filters into clean, normalized UI capabilities.
 * Sorts by `order` and normalizes property names to camelCase.
 *
 * @param {Object}      params
 * @param {FilterSupportContext} [params.capabilities]  - Context flags derived from dataset & config
 * @param {object}      params.fullFiltersRange - Derived dataset ranges per filter ID
 * @param {object|null} params.projectConfig - Configuration object from panel-config.json
 * @param {object|null} params.panelSettings - Configuration object from panel-settings.json
 * @returns {{ mainFilters: object[], promotedFilters: object[], additionalFilters: object[] }[]}
 */
function deriveFilters({
  capabilities = {},
  fullFiltersRange, 
  panelSettings, 
  projectConfig, 
}) {
  const supportedEntries = FILTER_REGISTRY.filter((entry) => {
    if (typeof entry.isSupported !== 'function') return true
    const range = fullFiltersRange?.[entry.id]
    return entry.isSupported(range, capabilities)
  })

  const registryMap = new Map(supportedEntries.map((item) => [item.id, item]))

  const rawFilters = 
    panelSettings?.catalog_filters?.filters || 
    projectConfig?.settings_defaults?.catalog_filters?.filters

  // const registryMap = new Map(FILTER_REGISTRY.map(item => [item.id, item]));
  if (Array.isArray(rawFilters) && rawFilters.length > 0) {
    const validEnabledFilters = rawFilters
      .filter((filter) => Boolean(filter?.is_active) && Boolean(filter?.id)) // discard non valid records
      .filter((filter) => {
        const registryItem = registryMap.get(filter.id)
        if (!registryItem) return false

        const range = fullFiltersRange?.[filter.id]
        return typeof registryItem.isSupported === 'function'
          ? registryItem.isSupported(range, capabilities)
          : true
      })

    const { main, promoted, additional } = validEnabledFilters.reduce(
      (acc, filter) => {
        if (BASE_MAIN_FILTER_IDS.includes(filter.id)) {
          acc.main.push(filter);
        } else if (filter.in_main_bar === true) {
          acc.promoted.push(filter);
        } else {
          acc.additional.push(filter);
        }
        return acc;
      },
      { main: [], promoted: [], additional: [] }
    );

    const sortAndMapToRegistry = (filters) => 
      [...filters]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((filter) => {
          const registryItem = registryMap.get(filter.id);
          
          if (!registryItem) {
            Logger.error({ 
              message: `Filter ID "${filter.id}" not found in filtersRegistry`, 
              content: { filter } 
            });
            return null;
          }
          
          return registryItem;
        })
        .filter(Boolean); // Potential null cleanup

    return {
      mainFilters: sortAndMapToRegistry(main),
      promotedFilters: sortAndMapToRegistry(promoted),
      additionalFilters: sortAndMapToRegistry(additional),
    };
  }

  const fallbackMain = []
  const fallbackAdditional = []

  for (const entry of supportedEntries) {
    if (BASE_MAIN_FILTER_IDS.includes(entry.id)) {
      fallbackMain.push(entry)
    } else {
      fallbackAdditional.push(entry)
    }
  }

  return {
    mainFilters: fallbackMain,
    promotedFilters: [],
    additionalFilters: fallbackAdditional,
  }

}

/**
 * Context passed to SORT_REGISTRY `isSupported` methods during derivation.
 *
 * @typedef {Object} SortSupportContext
 * @property {boolean} [favoritesSectionEnabled]  - Whether favorites presentation was commissioned, from appCapabilities.
 * @property {boolean} [isInsolationSupported]    - Whether insolation presentation was commissioned and data is present.
 * @property {boolean} [pricesVisible]            - Whether prices display is globally enabled.
 */
/**
 * Derives available catalog sorting parameters based on CMS settings and dataset capabilities.
 *
 * @param {Object}      params
 * @param {SortSupportContext} [params.capabilities]  - Context flags derived from dataset & config
 * @param {object|null} params.panelSettings - Configuration object from panel-settings.json
 * @param {object|null} params.projectConfig - Configuration object from panel-config.json
 * @returns {{ id: string, labelKey: string }[]}
 */
function deriveSorting({
  capabilities = {},
  panelSettings, 
  projectConfig, 
}) {

  const supportedEntries = SORT_REGISTRY.filter(
    (entry) => typeof entry.isSupported !== 'function' || entry.isSupported(capabilities)
  )
  const registryMap = new Map(supportedEntries.map((item) => [item.id, item]))

  const rawSortConfig = panelSettings?.catalog_sorting?.parameters;
  const sortAndMapToRegistry = (parameters) => 
    [...parameters]
      .filter((item) => item?.is_active !== false && Boolean(item?.id))
      .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
      .map((item) => {
        const entry = registryMap.get(item.id)

        if (!entry) {
          Logger.error({ 
            message: `Sorting parameter ID "${item.id}" not found in sortRegistry supported entries`, 
            content: { item } 
          });
          return null;
        }

        return {
          id: entry.id,
          labelKey: entry.labelKey,
        }
      })
      .filter(Boolean); // Potential null cleanup

  if (Array.isArray(rawSortConfig) && rawSortConfig.length > 0) {

    return sortAndMapToRegistry(rawSortConfig)
  }

  return supportedEntries.map((entry) => ({
    id: entry.id,
    labelKey: entry.labelKey,
  }))
}


// ── Pure derivation ────────────────────────────────────

/**
 * Derives Catalog domain capabilities based on project configuration and panel settings.
 * Pure function — used by `useCatalogCapabilities` and unit tests.
 *
 * @param {Object}      params
 * @param {object}      params.appCapabilities
 * @param {object}      params.fullFiltersRange
 * @param {object|null} params.products          - Full catalog products for dataset capability checks
 * @param {object|null} params.projectConfig     - Configuration object from panel-config.json
 * @param {object|null} params.panelSettings     - Configuration object from panel-settings.json
 * @param {object}      params.sortingRegistry
 * @returns {{
 *  additionalFilters:          object[],
 *  areSoldOutBuildingsActive:  boolean,
 *  areSoldOutTiersActive:      boolean,
 *  catalogFilters:             object[],
 *  catalogSortingParameters:   object[],
 *  cardDataItems:              object[], 
 *  defaultProductCategory:     string,
 *  hasActiveCatalogActions:    boolean,
 *  hasArrangeCommissioned:     boolean,
 *  hasAxonometricCommissioned: boolean,
 *  hasHotspotCommissioned:     boolean,
 *  hasInsolationCommissioned:  boolean,
 *  hasMultipleAreas:           boolean,
 *  hasMultipleBuildings:       boolean,
 *  hasMultipleGates:           boolean,
 *  hasMultipleTierSets:        boolean,
 *  listColumns:                object[],
 *  listStickyColumnsCount:     number,
 *  loadWithExpandedFilters:    boolean,
 *  loadViewModeDesktop:        sting,
 *  loadViewModeMobile:         sting,
 *  mainFilters:                object[], 
 *  pricesReservedVisible:      boolean,
 *  pricesSoldVisible:          boolean,
 *  pricesVisible:              boolean,
 *  promotedFilters:            object[], 
 *  tileDataItems:              object[],
 * 
 * }} Catalog capabilities object
 */
export function deriveCatalogCapabilities({
  appCapabilities,
  fullFiltersRange, 
  products, 
  projectConfig, 
  panelSettings, 
  sortingRegistry
}) {
  if (!projectConfig?.app_config) {
    return NULL_CAPABILITIES;
  }
  const favoritesSectionEnabled = appCapabilities?.favoritesSectionEnabled !== false;

  const appConfig = projectConfig.app_config ?? {};

  const hasArrangeCommissioned = appConfig.apartments?.compartments_versioning === true
  const hasAxonometricCommissioned = appConfig.apartments?.render_360?.enabled === true
  const hasHotspotCommissioned = appConfig.interiors?.render_ai?.enabled === true
  const hasInsolationCommissioned = appConfig.apartments?.floorplan_insolation === true
  
  const defaultProductCategory = panelSettings?.default_product_category?.category ?? PRODUCT_CATEGORY.APARTMENT_STANDARD;
  
  const areSoldOutBuildingsActive = panelSettings?.model_load_config?.sold_out_building_outlines_active !== false //move to appCapabilities??
  const areSoldOutTiersActive = panelSettings?.model_load_config?.sold_out_tiers_outlines_active !== false //move to appCapabilities??

  const datasetHasProductWithInsolation = asArray(products).some((p) => hasInsolationData(p));
  const isInsolationSupported = hasInsolationCommissioned && datasetHasProductWithInsolation;

  const hasMultipleAreas  = [...new Set((products ?? []).map(
    p => p.area_original).filter(Boolean))].length > 1
  const hasMultipleBuildings  = [...new Set((products ?? []).map(
    p => p.building_id).filter(Boolean))].length > 1
  const hasMultipleGates      = [...new Set((products ?? []).map(
    p => p.id_cad?.split('-')[1]).filter(Boolean))].length > 1
  const hasMultipleTierSets =  [...new Set((products ?? []).map(
    p => p.tiers?.map(t => t.ref_tier).sort((a, b) => a - b)).filter(Boolean))].length > 1
  const hasMultiplePhases = true; // more then one developent phase on sale
  
  const pricesVisibility = panelSettings?.prices_visibility ?? {}
  const pricesVisible =         pricesVisibility?.prices_visible          !== false;
  const pricesSoldVisible =     pricesVisibility?.prices_sold_visible     !== false;
  const pricesReservedVisible = pricesVisibility?.prices_reserved_visible !== false;

  const catalogLoadConfig = panelSettings?.catalog_load_config ?? {}
  const loadWithExpandedFilters = catalogLoadConfig?.load_with_expanded_filters === true;
  const loadViewModeDesktop = catalogLoadConfig?.load_with_view_desktop ?? VIEW_MODE.LIST;
  const loadViewModeMobile = catalogLoadConfig?.load_with_view_mobile ?? VIEW_MODE.CARDS;
  const listViewStickyColumnsCount = catalogLoadConfig?.list_sticky_columns_count ?? 0

  const isSupportedCapabilitiesContext = {
    favoritesSectionEnabled,
    hasMultipleBuildings,
    hasMultipleGates,
    isInsolationSupported,
    pricesVisible, 
  }
  const catalogFilters = deriveFilters({
    capabilities: isSupportedCapabilitiesContext,
    fullFiltersRange, 
    panelSettings, 
    projectConfig, 
  });
  const catalogSortingParameters = deriveSorting({
    capabilities: isSupportedCapabilitiesContext,
    panelSettings, 
    projectConfig, 
  });
  const catalogDisplay = deriveCatalogDisplay({ 
    capabilities: isSupportedCapabilitiesContext, 
    panelSettings, 
    projectConfig, 
  })

  return {
    areSoldOutBuildingsActive,
    areSoldOutTiersActive,
    ...catalogDisplay,
    ...catalogFilters,
    catalogFilters, //TODO: move Filters to use directly mainFilters, promotedFilters, additionalFilters,
    catalogSortingParameters,
    defaultProductCategory,
    hasArrangeCommissioned,
    hasAxonometricCommissioned,
    hasHotspotCommissioned,
    hasInsolationCommissioned,
    hasMultipleAreas,
    hasMultipleBuildings,
    hasMultipleGates,
    hasMultipleTierSets,
    listViewStickyColumnsCount,
    loadWithExpandedFilters,
    loadViewModeDesktop,
    loadViewModeMobile,
    pricesReservedVisible,
    pricesSoldVisible,
    pricesVisible,
  }
}